import { useCopies } from "../hooks/useCopies";
import React from "react";
import { getEnvironmentInfo } from "../utils/config";
import { useConsent } from "../../context/consent";
import { AppType } from "../utils/enums";
import { ConsentAcceptanceType } from "../enums/ConsentAcceptanceType";
import { useQueryParams } from "../hooks/useQueryParams/useQueryParams";
import { CookieManager } from "./enum";
import { ConsentManagementServiceType } from "./types";

export default function useConsentBannerContainerService(): ConsentManagementServiceType {
  const { queryParams } = useQueryParams();
  const copies = useCopies();
  const appType = React.useMemo(() => getEnvironmentInfo().appType, []);
  const {
    analyticalConsentProvided,
    grantFullConsent,
    consentCopy: data,
    canShowCookieConsentButton,
    hasAnyConsent,
    grantConsent,
    updateConsent,
    hasStoredCookieConsent
  } = useConsent();

  const consentCopy = React.useMemo(() => {
    switch (appType) {
      case AppType.CONSOLE:
        return copies.get("console.cookies.consent.body");
      default:
        return data?.gdprConfigMetadata?.copy || "";
    }
  }, [data?.gdprConfigMetadata, appType, copies]);

  const acceptClickHandler = React.useCallback(() => {
    grantFullConsent(ConsentAcceptanceType.CLICKED);
  }, [grantFullConsent]);

  React.useEffect(() => {
    if (queryParams.autoConsent && !analyticalConsentProvided) {
      grantFullConsent(ConsentAcceptanceType.FORWARDED);
    }
  }, [queryParams.autoConsent, grantFullConsent, analyticalConsentProvided]);

  const askForConsent = React.useMemo(() => {
    if (hasStoredCookieConsent || queryParams.autoConsent || data?.unknown) {
      return false;
    }
    switch (appType) {
      case AppType.MYEXPERIENCE:
      case AppType.REGISTRATION:
        return Boolean(data?.gdprConsentEnabled);
      default:
        return true;
    }
  }, [hasStoredCookieConsent, queryParams.autoConsent, data?.unknown, data?.gdprConsentEnabled, appType]);

  const consentManagementType = React.useMemo(() => {
    if (data?.unknown) {
      return CookieManager.UNKNOWN;
    }
    switch (appType) {
      case AppType.MYEXPERIENCE:
      case AppType.REGISTRATION:
        return data?.gdprConfigMetadata?.manager || CookieManager.ALLOW_ALL;
      default:
        return CookieManager.IN_HOUSE;
    }
  }, [appType, data]);

  return {
    askForConsent,
    acceptClickHandler,
    consentCopy,
    canShowCookieConsentButton,
    hasAnyConsent,
    analyticalConsentProvided,
    grantConsent,
    consentManagementType,
    updateConsent
  };
}
